// Triangulation of any simple polygon, holes included, and the merge of a
// triangulation back into convex pieces. Pure and DOM-free (unit-tested in
// test/triangulate.test.mjs).
//
// `earClip` returns triangles made of the INPUT points themselves (the same
// objects): a collinear point on a ring (a jamb on a wall, a crease crossing) is
// kept as a triangle corner, never dropped, so a neighbouring mesh built on the
// same ring meets this one vertex for vertex.

import type { Vec2 } from './types';

export type Tri = [Vec2, Vec2, Vec2];

/** Twice the signed area of triangle o-a-b: > 0 when it turns counter-clockwise. */
function cross(o: Vec2, a: Vec2, b: Vec2): number {
  return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
}

function signedArea(ring: readonly Vec2[]): number {
  let s = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) s += ring[j].x * ring[i].y - ring[i].x * ring[j].y;
  return s / 2;
}

const same = (p: Vec2, q: Vec2): boolean => p.x === q.x && p.y === q.y;

/** The ring without consecutive (or closing) duplicates, wound CCW or CW. */
function prepare(ring: readonly Vec2[], ccw: boolean, eps: number): Vec2[] {
  const out: Vec2[] = [];
  const near = (p: Vec2, q: Vec2): boolean => Math.abs(p.x - q.x) <= eps && Math.abs(p.y - q.y) <= eps;
  for (const p of ring) if (!out.length || !near(p, out[out.length - 1])) out.push(p);
  while (out.length > 1 && near(out[0], out[out.length - 1])) out.pop();
  if (out.length < 3) return [];
  if ((signedArea(out) > 0) !== ccw) out.reverse();
  return out;
}

/** Is `m` inside the interior angle of the CCW ring at index k? */
function locallyInside(ring: readonly Vec2[], k: number, m: Vec2): boolean {
  const n = ring.length;
  const a = ring[(k + n - 1) % n], v = ring[k], b = ring[(k + 1) % n];
  const l1 = cross(v, b, m) >= 0, l2 = cross(a, v, m) >= 0;
  return cross(a, v, b) >= 0 ? l1 && l2 : l1 || l2;
}

/** Joins a CW hole into the CCW ring through a bridge from the hole's
 *  rightmost vertex M to a ring vertex it can see (Eberly): the nearest ring
 *  edge hit by the ray from M toward +x, unless a ring vertex inside the
 *  triangle that hit leaves blocks it (then the one of least angle to the
 *  ray). The bridge is walked both ways, so the
 *  ring stays one closed loop with M and its partner listed twice. */
function bridgeHole(ring: Vec2[], hole: Vec2[], eps: number, epsA: number): Vec2[] {
  const n = ring.length;
  let mi = 0;
  for (let i = 1; i < hole.length; i++) if (hole[i].x > hole[mi].x || (hole[i].x === hole[mi].x && hole[i].y < hole[mi].y)) mi = i;
  const M = hole[mi];
  let hx = Infinity, hit = -1;
  for (let i = 0; i < n; i++) {
    const p = ring[i], q = ring[(i + 1) % n];
    if (p.y === q.y) {
      if (p.y !== M.y) continue;
      const x = Math.min(p.x, q.x) >= M.x ? Math.min(p.x, q.x) : Math.max(p.x, q.x);
      if (x >= M.x && x < hx) { hx = x; hit = i; }
      continue;
    }
    if ((p.y < M.y && q.y < M.y) || (p.y > M.y && q.y > M.y)) continue;
    const x = p.x + ((M.y - p.y) * (q.x - p.x)) / (q.y - p.y);
    if (x >= M.x && x < hx) { hx = x; hit = i; }
  }
  if (hit < 0) return ring; // the hole is not inside the ring: nothing to join
  const p = ring[hit], q = ring[(hit + 1) % n];
  const at = (v: Vec2): boolean => Math.abs(v.x - hx) <= eps && Math.abs(v.y - M.y) <= eps;
  let P = at(p) ? hit : at(q) ? (hit + 1) % n : (p.x >= q.x ? hit : (hit + 1) % n);
  if (!at(ring[P])) {
    // A ring vertex inside triangle M-I-P can block the view of P; the one of
    // least angle to the ray is visible.
    const A = M, B = { x: hx, y: M.y }, C = ring[P];
    const s = cross(A, B, C) > 0 ? 1 : -1;
    let best = Infinity, bestD = Infinity;
    for (let k = 0; k < n; k++) {
      const r = ring[k];
      const dx = r.x - M.x, dy = r.y - M.y;
      if (k === P || same(r, C) || dx <= 0) continue;
      if (s * cross(A, B, r) < -epsA || s * cross(B, C, r) < -epsA || s * cross(C, A, r) < -epsA) continue;
      if (!locallyInside(ring, k, M)) continue;
      const tan = Math.abs(dy) / dx, d = Math.hypot(dx, dy);
      if (tan < best || (tan === best && d < bestD)) { best = tan; bestD = d; P = k; }
    }
  }
  // The partner may be listed twice (an earlier bridge's end): take the copy
  // whose interior angle the bridge leaves through.
  if (!locallyInside(ring, P, M)) {
    for (let k = 0; k < n; k++) if (same(ring[k], ring[P]) && locallyInside(ring, k, M)) { P = k; break; }
  }
  const loop = [...hole.slice(mi), ...hole.slice(0, mi), M];
  return [...ring.slice(0, P + 1), ...loop, ring[P], ...ring.slice(P + 1)];
}

/** Ear clipping of one CCW ring (bridges and collinear points allowed). A
 *  point is off a line when twice the area it spans with a run of it exceeds
 *  `epsA`, or — with `tol`, a distance — when it stands more than `tol` off it. */
function clipEars(P: Vec2[], epsA: number, tol?: number): Tri[] {
  const n = P.length;
  const prev = P.map((_, i) => (i + n - 1) % n), next = P.map((_, i) => (i + 1) % n);
  const out: Tri[] = [];
  let count = n;
  const off = (a: Vec2, b: Vec2): number => (tol === undefined ? epsA : tol * Math.hypot(b.x - a.x, b.y - a.y));
  const turn = (i: number): number => cross(P[prev[i]], P[i], P[next[i]]);
  const thr = (i: number): number => off(P[prev[i]], P[next[i]]);
  const unlink = (i: number): number => { next[prev[i]] = next[i]; prev[next[i]] = prev[i]; count--; return next[i]; };
  const emit = (i: number): number => { out.push([P[prev[i]], P[i], P[next[i]]]); return unlink(i); };
  /** Does another ring point lie in the ear at i — on its boundary too, unless `strict`? */
  const blocked = (i: number, strict: boolean): boolean => {
    const a = P[prev[i]], b = P[i], c = P[next[i]];
    for (let j = next[next[i]]; j !== prev[i]; j = next[j]) {
      const p = P[j];
      if (same(p, a) || same(p, b) || same(p, c)) continue;
      const c1 = cross(a, b, p), c2 = cross(b, c, p), c3 = cross(c, a, p);
      const e1 = off(a, b), e2 = off(b, c), e3 = off(c, a);
      if (strict ? c1 > e1 && c2 > e2 && c3 > e3 : c1 >= -e1 && c2 >= -e2 && c3 >= -e3) return true;
    }
    return false;
  };
  /** No ear in a whole lap — only degenerate input gets here. Undo the
   *  degeneracy one vertex at a time: a spike, then a relaxed ear, then a
   *  straight point, then the most convex corner. */
  const rescue = (start: number): number => {
    const live: number[] = [];
    let j = start;
    do { live.push(j); j = next[j]; } while (j !== start);
    const flat = (k: number): boolean => Math.abs(turn(k)) <= thr(k);
    const dot = (k: number): number => {
      const a = P[prev[k]], b = P[k], c = P[next[k]];
      return (b.x - a.x) * (c.x - b.x) + (b.y - a.y) * (c.y - b.y);
    };
    for (const k of live) if (flat(k) && dot(k) <= 0) return unlink(k);
    for (const k of live) if (turn(k) > thr(k) && !blocked(k, true)) return emit(k);
    for (const k of live) if (flat(k)) return unlink(k);
    let best = -1;
    for (const k of live) if (turn(k) > thr(k) && (best < 0 || turn(k) > turn(best))) best = k;
    return best < 0 ? -1 : emit(best);
  };
  let i = 0, miss = 0;
  while (count > 3) {
    if (turn(i) > thr(i) && !blocked(i, false)) { i = emit(i); miss = 0; continue; }
    i = next[i];
    if (++miss < count) continue;
    const r = rescue(i);
    if (r < 0) return out;
    i = r; miss = 0;
  }
  if (turn(i) > thr(i)) out.push([P[prev[i]], P[i], P[next[i]]]);
  return out;
}

/** Triangles tiling a simple polygon with optional holes, counter-clockwise,
 *  built from the input points. Either winding is accepted for the outline and
 *  for each hole; repeated consecutive points and collinear points are fine.
 *  By default a point counts as on a line within an area relative to the
 *  polygon's extent; `tol` (a distance) measures it by how far it stands off
 *  instead, so a corner far smaller than the polygon is still a corner. */
export function earClip(outer: readonly Vec2[], holes: ReadonlyArray<readonly Vec2[]> = [], tol?: number): Tri[] {
  const j = joined(outer, holes);
  return j ? clipEars(j.ring, j.epsA, tol) : [];
}

/** A polygon with holes as ONE counter-clockwise ring: each hole joined to the
 *  outline by a bridge walked both ways (the ring `earClip` clips), built from
 *  the input points — a bridge's two ends are listed twice. Empty when the
 *  outline has no extent or under three points. */
export function bridgeHoles(outer: readonly Vec2[], holes: ReadonlyArray<readonly Vec2[]>): Vec2[] {
  return joined(outer, holes)?.ring ?? [];
}

function joined(outer: readonly Vec2[], holes: ReadonlyArray<readonly Vec2[]>): { ring: Vec2[]; epsA: number } | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const ring of [outer, ...holes]) for (const p of ring) {
    x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y);
  }
  const ext = Math.max(x1 - x0, y1 - y0);
  if (!(ext > 0) || !isFinite(ext)) return null;
  const eps = ext * 1e-9, epsA = ext * ext * 1e-12;
  let ring = prepare(outer, true, eps);
  if (ring.length < 3) return null;
  const maxX = (r: Vec2[]): number => Math.max(...r.map((p) => p.x));
  const hs = holes.map((h) => prepare(h, false, eps)).filter((h) => h.length >= 3).sort((a, b) => maxX(b) - maxX(a));
  for (const h of hs) ring = bridgeHole(ring, h, eps, epsA);
  return { ring, epsA };
}

/** Hertel–Mehlhorn: merges a triangulation into convex pieces by dropping each
 *  shared edge whose two ends stay convex (a straight angle counts as convex).
 *  Pieces are counter-clockwise; neighbours meet on shared points exactly. */
export function convexMerge(tris: ReadonlyArray<readonly [Vec2, Vec2, Vec2]>): Vec2[][] {
  const key = (p: Vec2): string => `${p.x},${p.y}`;
  const ek = (p: Vec2, q: Vec2): string => `${key(p)}|${key(q)}`;
  const polys: Array<Vec2[] | null> = tris.map((t) => (cross(t[0], t[1], t[2]) < 0 ? [t[0], t[2], t[1]] : [t[0], t[1], t[2]]));
  const owner = new Map<string, number>();
  const own = (i: number, on: boolean): void => {
    const r = polys[i]!;
    for (let k = 0; k < r.length; k++) {
      const e = ek(r[k], r[(k + 1) % r.length]);
      if (on) owner.set(e, i); else if (owner.get(e) === i) owner.delete(e);
    }
  };
  polys.forEach((_, i) => own(i, true));
  const convexAt = (a: Vec2, b: Vec2, c: Vec2): boolean =>
    cross(a, b, c) >= -1e-9 * Math.hypot(b.x - a.x, b.y - a.y) * Math.hypot(c.x - b.x, c.y - b.y);
  for (let i = 0; i < polys.length; i++) {
    let merged = true;
    while (merged && polys[i]) {
      merged = false;
      const A = polys[i]!;
      for (let e = 0; e < A.length && !merged; e++) {
        const p = A[e], q = A[(e + 1) % A.length];
        const j = owner.get(ek(q, p));
        if (j == null || j === i || !polys[j]) continue;
        const B = polys[j]!;
        const f = B.findIndex((v, k) => key(v) === key(q) && key(B[(k + 1) % B.length]) === key(p));
        if (f < 0) continue;
        const U: Vec2[] = [];
        for (let k = 1; k <= A.length; k++) U.push(A[(e + k) % A.length]); // q … p
        for (let k = 2; k < B.length; k++) U.push(B[(f + k) % B.length]); // after p, up to q
        const m = U.length, jp = A.length - 1;
        if (!convexAt(U[m - 1], U[0], U[1]) || !convexAt(U[jp - 1], U[jp], U[(jp + 1) % m])) continue;
        if (U.some((v, k) => key(v) === key(U[(k + 1) % m]))) continue;
        own(i, false); own(j, false);
        polys[i] = U; polys[j] = null;
        own(i, true);
        merged = true;
      }
    }
  }
  return polys.filter((r): r is Vec2[] => !!r);
}
